import { useCallback, useRef } from "react";

const DEFAULT_LONG_PRESS_MS = 400;

/**
 * Distinguishes between short taps and long presses on touch devices.
 * Returns `onPointerDown` / `onPointerUp` handlers that call `onTap` or
 * `onLongPress` depending on how long the pointer was held.
 *
 * @typeParam T - The type of the item being interacted with
 */
export function useMobileLongPress<T>({
    onTap,
    onLongPress,
    delayMs = DEFAULT_LONG_PRESS_MS,
}: {
    onTap: (item: T) => void;
    onLongPress: (item: T) => void;
    delayMs?: number;
}) {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingRef = useRef<T | null>(null);

    const handlePointerDown = useCallback(
        (item: T) => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            pendingRef.current = item;
            timerRef.current = setTimeout(() => {
                pendingRef.current = null;
                timerRef.current = null;
                onLongPress(item);
            }, delayMs);
        },
        [onLongPress, delayMs],
    );

    const handlePointerUp = useCallback(() => {
        const pending = pendingRef.current;
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        if (pending !== null) {
            pendingRef.current = null;
            onTap(pending);
        }
    }, [onTap]);

    return { onPointerDown: handlePointerDown, onPointerUp: handlePointerUp };
}
